/**
 * Bosnian strings for the two home screens.
 *
 * Two dashboards, chosen by role — a worker's and a supervisor's — so a few
 * keys look like near-duplicates and are not: "Moje prijave" is the worker's
 * own reports, "Van roka" is the supervisor's late queue, and they are
 * different questions that happen to be about the same noun.
 *
 * The status badge on a task or a report is NOT here. `statusLabel` comes from
 * the server with the row, and the only thing this screen adds is the overdue
 * override beside it.
 *
 * Nothing here puts a person's name in front of a verb. "Amra je prijavila"
 * needs the reporter's gender, which the application does not know — so the
 * activity list names the event and then the person: "Nova prijava · Amra".
 */

export const home = {
  // The first name only — `user.name.split(' ')[0]` — which is what makes the
  // interpolation worth having rather than a plain concatenation.
  'home.greeting': 'Dobar dan, {name}',

  // ---- Worker -------------------------------------------------------
  'home.report.title': 'Prijavi problem',
  'home.report.text': 'Kvar, incident, bezbjednost ili prijedlog za poboljšanje',
  'home.scan.title': 'Skeniraj QR kod',
  'home.scan.text': 'Otvori prijavu za mašinu pored koje stojiš',

  'home.mine.title': 'Moje prijave',
  'home.mine.all': 'Sve moje →',
  'home.stat.inProgress': 'U obradi',
  'home.mine.resolved': 'Riješene',
  'home.recent.empty': 'Još niste poslali nijednu prijavu',
  'home.recent.emptyText': 'Kada prijavite kvar ili pošaljete prijedlog, pojaviće se ovdje sa statusom.',

  /*
   * The rating card. "Ovaj mjesec" in the title, because the ranking under it
   * covers twelve months and the two figures would otherwise read as one
   * number that disagrees with itself.
   */
  'home.rating.title': 'Moja ocjena ovaj mjesec',
  'home.rating.empty': 'Ocjena se pojavi nakon prvog obilaska ili zadatka u mjesecu.',
  'home.rating.noWalks': 'Još nijedan obilazak',
  'home.rating.noTasks': 'Nema dodijeljenih zadataka',
  'home.rating.tasksDone': '{done} od {total} završeno',
  'home.rating.proposals': 'Prijedlozi',
  'home.rating.accepted.one': '{count} prihvaćen',
  'home.rating.accepted.few': '{count} prihvaćena',
  'home.rating.accepted.many': '{count} prihvaćenih',
  'home.rating.points': '{points} poena ukupno',

  // ---- Supervisor ---------------------------------------------------
  'home.today': 'Za vas danas',

  // "Sve" means the whole plant, not this person's own — the worker's
  // dashboard uses the same word for their own count.
  'home.stat.openSubmissions.explain': 'Sve otvorene prijave iz pogona koje vidite, ne samo vaše.',
  'home.stat.overdue': 'Van roka',
  'home.stat.overdue.needsAttention': 'Zahtijeva pažnju danas',
  'home.stat.overdue.allOnTime': 'Sve u roku',
  // A nought here can mean "nothing is late" or "nothing has a deadline", and
  // the tile turns green either way.
  'home.stat.overdue.explain':
    'Otvorene prijave kojima je rok prošao; prijave bez upisanog roka se ovdje ne broje.',
  'home.stat.fresh': 'Nove',
  'home.stat.fresh.hint': 'Čekaju prvi pregled',
  'home.stat.unassigned': 'Nedodijeljene',
  'home.stat.unassigned.hint': 'Od {open} otvorenih',
  'home.stat.myTasks': 'Moji zadaci',
  'home.stat.myTasks.explain': 'Zadaci dodijeljeni vama koji još nisu završeni.',

  'home.overdue.title': 'Najduže van roka',
  'home.overdue.all': 'Sve van roka ({count}) →',
  'home.overdue.empty': 'Ništa nije van roka',
  'home.overdue.emptyText': 'Prijave kojima prođe rok pojaviće se ovdje, najstarije prve.',
  'home.overdue.nobody': 'nije dodijeljena',
  'home.overdue.late.one': 'kasni {count} dan',
  'home.overdue.late.few': 'kasni {count} dana',
  'home.overdue.late.many': 'kasni {count} dana',

  'home.fiveS.month': '5S · {month}',
  'home.fiveS.board': '5S tabla →',
  // The big number in front is how many were walked; this is the rest of the
  // sentence, which agrees with the number of machines.
  'home.fiveS.walkedOf.one': 'od {count} mašine obiđeno',
  'home.fiveS.walkedOf.few': 'od {count} mašine obiđeno',
  'home.fiveS.walkedOf.many': 'od {count} mašina obiđeno',
  'home.fiveS.progress': 'Mašine obiđene ovaj mjesec',
  'home.fiveS.average': 'Prosjek obilazaka ovog mjeseca: {value}',
  'home.fiveS.allWalked': 'Sve mašine su obiđene ovaj mjesec.',
  'home.fiveS.toWalk': 'Još nisu obiđene',
  'home.fiveS.more': '+{count} na 5S tabli →',
  'home.fiveS.noMachines': 'U vašim pogonima nema aktivnih mašina.',

  // Follows how many plants the reader sees, like the 5S average label below.
  'home.activity.title': 'Nedavno u vašem pogonu',
  'home.activity.title.several': 'Nedavno u vašim pogonima',
  'home.activity.title.all': 'Nedavno u svim pogonima',
  'home.activity.empty': 'Još se ništa nije desilo',
  'home.activity.emptyText': 'Nove prijave, promjene statusa i 5S obilasci pojaviće se ovdje.',
  'home.activity.reported': 'Nova prijava {ref}',
  'home.activity.moved': 'Promjena statusa {ref}',
  'home.activity.walked': '5S obilazak',

  // ---- Shared cards -------------------------------------------------
  'home.tasks.title': 'Moji zadaci',
  'home.tasks.all': 'Svi zadaci →',
  'home.tasks.due': 'Rok: {date}',
  'home.tasks.noDue': 'Bez roka',
  'home.tasks.overdue': 'Van roka',
  'home.tasks.lateCount.one': '{count} van roka',
  'home.tasks.lateCount.few': '{count} van roka',
  'home.tasks.lateCount.many': '{count} van roka',
  'home.tasks.soonCount.one': '{count} s rokom ove sedmice',
  'home.tasks.soonCount.few': '{count} s rokom ove sedmice',
  'home.tasks.soonCount.many': '{count} s rokom ove sedmice',
  'home.tasks.nothingSoon': 'Ništa hitno ove sedmice',
  'home.tasks.empty': 'Nemate otvorenih zadataka',
  'home.tasks.emptyText': 'Zadaci koje vam neko dodijeli uz prijavu pojaviće se ovdje.',

  // Context, not a ranking: nobody is told where they stand relative to anyone.
  'home.fiveS.plant': 'Prosjek pogona',
  // Said out loud, because a second number beside your own on a screen that
  // also carries a public ranking is read as a position until it says it is not.
  'home.fiveS.explain':
    'Prosjek pored stoji samo da imate s čim uporediti svoj broj — računa se iz pogona koje vidite, nije rang i nigdje ne piše na kojem ste mjestu.',

  // ---- The rating, and the ranking everybody can see ----------------
  'leaderboard.title': 'Rang lista',
  // The formula, printed. A number people are ranked by has to be arguable,
  // and it cannot be argued with unless it says what it is made of.
  'leaderboard.formula':
    'Osnova: 5S {fiveS} i završeni zadaci {tasks}. Prihvaćeni prijedlozi dodaju do +{bonus} bodova ({accepted} prihvaćena prijedloga za punu premiju) — prijedlozi mogu samo podići ocjenu, nikada je ne smanjuju.',
  /*
   * The three things the printed formula cannot say in one line.
   *
   * It states the weights and the size of the bonus; it does not state what the
   * 5S half is a fact *about*, what a refused proposal costs, or what happens
   * when somebody has no walks at all. All three are the difference between a
   * public ranking people can argue with and one they can only resent — which
   * is why this is a note on the screen rather than a tooltip nobody opens.
   *
   * Longer than the guide's version of the same rules on purpose: `/uputstvo`
   * is read once, this is read beside the number.
   */
  'leaderboard.explain.note':
    'Dio za 5S govori o mašinama koje je neko održavao, a ne o njemu — ko je prešao na zapušten centar ima nizak procenat upravo zbog toga. Prihvaćeni prijedlozi se dodaju na osnovu i mogu je samo podići: odbijen prijedlog ne oduzima ništa. Ko nije bio ni na jednom obilasku ili nema nijedan zadatak, ocjenjuje se samo po onome što ima, a dio koji nedostaje se ne računa kao nula.',
  // Standard competition ranking, in the one form anybody can check against the
  // list in front of them.
  'leaderboard.explain.ties':
    'Isti rezultat dijeli isto mjesto: dvoje sa 82 su oboje drugi, a sljedeći je četvrti.',
  'leaderboard.part.fiveS': '5S',
  'leaderboard.part.bonus': '+{bonus} za {accepted} prihvaćena',
  'leaderboard.part.followThrough': 'Zadaci',
  'leaderboard.you': '(vi)',
  'leaderboard.thin': 'malo obilazaka',
  'leaderboard.showAll': 'Prikaži sve ({count})',
  'leaderboard.showLess': 'Prikaži manje',
  'leaderboard.rating': 'Ocjena',
  'leaderboard.position': 'Mjesto',

  // The average beside a person's own 5S figure is worked out in the plants
  // they see, so its label follows how many that is: one plant is that
  // plant's average, every plant is the company's.
  'home.fiveS.plant.several': 'Prosjek vaših pogona',
  'home.fiveS.plant.all': 'Prosjek firme',
} as const

export type HomeSlice = Record<keyof typeof home, string>
